import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Candidate, Mission, CandidateApplication } from '../interfaces/candidate.interface';
import { DateService } from '../services/date.service';
import { MessageDialogComponent } from '../../../shared/components/message-dialog';
import { MatDialog } from '@angular/material/dialog';
import { MaterialModule } from '../../../material/material.module';
import { DetailsComponent } from '../../../pages/etablissement/candidatures/details/details.component';
import { EstablishmentService } from '../../../services/establishment.service';
import { ChatService } from '../../../services/chat.service';
import { PermissionService } from '../../../services/permission.service';

// Ajouter cette interface au début du fichier ou dans un fichier d'interfaces
export interface CellAction {
  candidate: Candidate;
  date: string;
  status: string;
}

export interface EditContractRequest {
  candidate: Candidate;
  date: string;
  applicationId: number;
}

// Dans la classe CandidateTableComponent

@Component({
  selector: 'app-candidate-table',
  standalone: true,
  imports: [CommonModule, MaterialModule],
  template: `
    <div class="table-container">
      <table class="candidates-table">
        <thead>
          <tr>
            <th class="date-column">Les dates de cette mission</th>
            <th *ngFor="let candidate of candidates" class="candidate-column">
              <div class="candidate-header">
                <h4>{{ candidate.name }}</h4>
                <div class="candidate-info">
                  <div class="row">
                    <div class="col-6">
                  <div><strong>Nom :</strong> {{ candidate.name }}</div>
                  <div><strong>Prénom :</strong> {{ candidate.firstName }}</div>
                  </div>
                  <div class="col-6">
                    <span class="chat-icon-wrapper" *ngIf="isChatActive" matTooltip="Envoyer un message au candidat" (click)="openMessageDialog(candidate, $event)">
                      <mat-icon class="chat-icon">chat_bubble</mat-icon>
                    </span>
                  </div>
                  </div>
                  <!--<div><strong>Note :</strong> {{ candidate.note }}</div>
                  <a href="#" class="details-link">Voir plus de détails</a>-->
                </div>
              </div>
            </th>
          </tr>
          <tr>
            <th>Actions</th>
            <td *ngFor="let candidate of candidates" class="actions-cell">
              <div class="actions-list">
                <button 
                 class="action-btn details" 
                  (click)="openDetailCandidate(candidate)">
                  Voir les détails de ce candidat
                </button>
                <button 
                  class="action-btn accept"
                  [disabled]="!canAccept(candidate)"
                  (click)="onAcceptCandidate(candidate)">
                  Accepter cette candidature
                </button>
                <button 
                  class="action-btn reject"
                  [disabled]="!canReject(candidate)"
                  (click)="onRejectCandidate(candidate)">
                  Refuser cette candidature
                </button>
                <button 
                  class="action-btn cancel-accepted"
                  [disabled]="!canCancelAccepted(candidate)"
                  [title]="mayCancel ? '' : 'Vous n’avez pas le droit « Annuler une candidature (acceptée et/ou signée) »'"
                  (click)="onCancelAccepted(candidate)">
                  Annuler les dates acceptées
                </button>
                <button 
                  class="action-btn cancel-signed"
                  [disabled]="!canCancelSigned(candidate)"
                  [title]="mayCancel ? '' : 'Vous n’avez pas le droit « Annuler une candidature (acceptée et/ou signée) »'"
                  (click)="onCancelSigned(candidate)">
                  Annuler les dates affectées signées
                </button>
              </div>
            </td>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let date of missionDates">
            <td class="date-cell">
              {{ formatDate(date) }}
              <span *ngIf="getDailyHoursForDate(date) as dh" class="daily-hours">
                — HD {{ dh.startTime?.substring(0,5) }}
                <span *ngIf="dh.pauseTime && dh.pauseTime > 0"> ⏸ {{ dh.pauseTime }}min</span>
                → HF {{ dh.endTime?.substring(0,5) }}
              </span>
            </td>
            <td *ngFor="let candidate of candidates" class="status-cell">
              <div class="status-container">
                <div 
                  class="status-indicator"
                  [ngClass]="getStatusClass(candidate, date)"
                  [title]="getStatusTitle(candidate, date)"
                  (click)="onCellClick(candidate, date)">
                  <span *ngIf="getStatusIcon(candidate, date)" class="status-icon">
                    {{ getStatusIcon(candidate, date) }}
                  </span>
                </div>
                <div class="contract-buttons" *ngIf="shouldShowEditContractButton(candidate, date)">
                  <button
                    class="contract-btn edit-contract-btn"
                    (click)="onEditContract(candidate, date, $event)"
                    title="Modifier et valider les contrats">
                    <mat-icon class="contract-icon">edit</mat-icon>
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    
    </div>
          <div class="legend">
        <div class="legend-item">
          <div class="legend-color unavailable"></div>
          <span>Non disponible</span>
        </div>
        <div class="legend-item">
          <div class="legend-color no-application"></div>
          <span>Pas de candidature</span>
        </div>
        <div class="legend-item">
          <div class="legend-color proposed"></div>
          <span>Candidature proposée</span>
        </div>
        <div class="legend-item">
          <div class="legend-color rejected"></div>
          <span>Candidature annulée car refusée ou déjà affectée</span>
        </div>
        <div class="legend-item">
          <div class="legend-color accepted"></div>
          <span>Date acceptée non signée</span>
        </div>
        <div class="legend-item">
          <div class="legend-color contract-pending"></div>
          <span>Contrat en attente de validation</span>
        </div>
        <div class="legend-item">
          <div class="legend-color signed"></div>
          <span>Date affectée signée</span>
        </div>
      </div>
  `,
  styles: [`
    .table-container {
      width: 100%;
      overflow-x: auto;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    }

    .candidates-table {
      width: 100%;
      border-collapse: collapse;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    .candidates-table th,
    .candidates-table td {
      border: 1px solid #ddd;
      padding: 12px 8px;
      text-align: left;
      vertical-align: top;
    }

    .candidates-table th {
      background-color: #f8f9fa;
      font-weight: 600;
      color: #495057;
    }

    .date-column {
      width: 180px;
      background-color: #e9ecef !important;
    }

    .candidate-column {
      width: 250px;
      background-color: #f8f9fa !important;
    }

    .candidate-header h4 {
      margin: 0 0 8px 0;
      font-size: 16px;
      color: #212529;
    }

    .candidate-info {
      font-size: 12px;
      line-height: 1.4;
      color: #6c757d;
    }

    .candidate-info div {
      margin-bottom: 2px;
    }

    .details-link {
      color: #007bff;
      text-decoration: none;
      font-size: 11px;
    }

    .details-link:hover {
      text-decoration: underline;
    }

    .actions-cell {
      background-color: #f8f9fa;
    }

    .actions-list {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .action-btn {
      padding: 4px 8px;
      border: none;
      border-radius: 4px;
      font-size: 11px;
      cursor: pointer;
      text-align: left;
      transition: all 0.2s ease;
    }

    .action-btn:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .action-btn.details {
      background-color: #6c757d;
      color: white;
    }

    .action-btn.accept {
      background-color: #28a745;
      color: white;
    }

    .action-btn.accept:hover:not(:disabled) {
      background-color: #218838;
    }

    .action-btn.reject {
      background-color: #dc3545;
      color: white;
    }

    .action-btn.reject:hover:not(:disabled) {
      background-color: #c82333;
    }

    .action-btn.cancel-accepted,
    .action-btn.cancel-signed {
      background-color: #fd7e14;
      color: white;
    }

    .action-btn.cancel-accepted:hover:not(:disabled),
    .action-btn.cancel-signed:hover:not(:disabled) {
      background-color: #e8650e;
    }

    .date-cell {
      font-weight: 500;
      background-color: #f8f9fa;
    }

    .daily-hours {
      font-weight: 400;
      font-size: 11px;
      color: #007bff;
      white-space: nowrap;
    }

    .status-cell {
      text-align: center;
      padding: 8px;
    }

    .status-indicator {
      width: 30px;
      height: 30px;
      border-radius: 4px;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto;
      font-weight: bold;
      color: white;
      cursor: pointer;
    }

    .status-indicator.UNAVAILABLE {
      background-color: #dc3545;
    }

    .status-indicator.NO_APPLICATION {
      background-color: #343a40;
    }

    .status-indicator.PROPOSED {
      background-color: #fd7e14;
    }

    .status-indicator.REJECTED {
      background-color: #fd7e14;
      position: relative;
    }

    .status-indicator.ACCEPTED {
      background-color: #007bff;
    }

    .status-indicator.CONTRACT_PENDING_VALIDATION {
      background-color: #8b5cf6;
    }

    .status-indicator.SIGNED {
      background-color: #28a745;
    }

    .edit-contract-btn {
      background-color: #8b5cf6;
    }

    .edit-contract-btn:hover {
      background-color: #7c3aed;
    }

    .status-icon {
      font-size: 18px;
      font-weight: bold;
    }
    
    .status-container {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }
    
    .contract-buttons {
      position: absolute;
      right: 0;
      display: flex;
      gap: 4px;
    }
    
    .contract-btn {
      height: 24px;
      padding: 0 4px;
      border: none;
      background-color: #6c757d;
      color: white;
      border-radius: 4px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background-color 0.2s;
    }
    
    .contract-btn:hover {
      background-color: #5a6268;
    }
    
    .contract-icon {
      font-size: 16px;
      height: 16px;
      width: 16px;
    }

    .contract-text {
      margin: 0 !important;
    }

    .legend {
      margin-top: 16px;
      display: flex;
      flex-wrap: wrap;
      gap: 16px;
      padding: 16px;
      background-color: #f8f9fa;
      border: 1px solid #ddd;
      border-radius: 8px;
      box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    }

    .legend-item {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
    }

    .legend-color {
      width: 20px;
      height: 20px;
      border-radius: 3px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .legend-color.unavailable {
      background-color: #dc3545;
    }

    .legend-color.no-application {
      background-color: #343a40;
    }

    .legend-color.proposed {
      background-color: #fd7e14;
    }

    .legend-color.rejected {
      background-color: #fd7e14;
      position: relative;
    }

    .legend-color.rejected::after {
      content: '×';
      color: white;
      font-weight: bold;
      font-size: 14px;
    }

    .legend-color.accepted {
      background-color: #007bff;
    }

    .legend-color.contract-pending {
      background-color: #8b5cf6;
    }

    .legend-color.signed {
      background-color: #28a745;
    }

    .cdk-overlay-pane {
  margin: 0 !important;
  padding: 0 !important;
  top: 0 !important;
  box-shadow: none !important;
}

.chat-popover-panel {
  margin: 0 !important;
  padding: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
}

.message-dialog-container {
  margin: 0 !important;
  padding-top: 0 !important;
  box-shadow: none !important;
}

.chat-icon-wrapper {
  color: #0688E6;
}

.mat-dialog-container {
  margin: 0 !important;
  padding: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
}
  `]
})
export class CandidateTableComponent {

  @Input() candidates: Candidate[] = [];
  @Input() mission!: Mission;
  @Output() acceptCandidate = new EventEmitter<Candidate>();
  @Output() rejectCandidate = new EventEmitter<Candidate>();
  @Output() cancelAccepted = new EventEmitter<Candidate>();
  @Output() cancelSigned = new EventEmitter<Candidate>();
  @Output() cellActionRequested = new EventEmitter<CellAction>();
  @Output() editContractRequested = new EventEmitter<EditContractRequest>();
  isChatActive: boolean = true;
  
  constructor(
    private dialog: MatDialog,
    private establishmentService: EstablishmentService,
    private chatService: ChatService,
    private permissionService: PermissionService
  ) {
  }

  /** « Répondre aux missions » : accepter / refuser une candidature. Le back reste l'autorité (403). */
  get mayRespond(): boolean {
    return this.permissionService.has('MISSION_VIEW');
  }

  /** « Annuler une candidature (acceptée et/ou signée) » : annuler des dates acceptées ou signées. Le back reste l'autorité (403). */
  get mayCancel(): boolean {
    return this.permissionService.has('MISSION_CANCEL');
  }

  ngAfterViewInit(): void {
    // Subscribe to chat active status
    this.chatService.chatActive$.subscribe((isActive: boolean) => {
      this.isChatActive = isActive;
    });
    this.isChatActive = this.chatService.isChatActive;
  }

  get missionDates(): string[] {
    if (!this.mission?.startDate || !this.mission?.endDate) {
      return [];
    }
    return DateService.generateDateRange(this.mission.startDate, this.mission.endDate);
  }

  formatDate(dateStr: string): string {
    return DateService.formatDate(dateStr);
  }

  getDailyHoursForDate(dateStr: string): { startTime: string; endTime: string; pauseTime?: number } | null {
    // First check if there's a specific dailyHours entry for this date (per-day case)
    if (this.mission?.dailyHours && this.mission.dailyHours.length > 0) {
      const found = this.mission.dailyHours.find(dh => dh.date === dateStr);
      if (found) return found;
    }
    // Fall back to mission-level start/end time (same-hours case)
    if (this.mission?.startTime && this.mission?.endTime) {
      return { startTime: this.mission.startTime, endTime: this.mission.endTime, pauseTime: this.mission.pauseTime };
    }
    return null;
  }

  getStatusClass(candidate: Candidate, date: string): string {
    const application = this.getApplicationForDate(candidate, date);
    if (!application) return 'NO_APPLICATION';
    
    // Check if there are any SIGNED applications for this date across all candidates
    const hasSignedApplicationForDate = this.candidates.some(otherCandidate => {
      const otherApplication = this.getApplicationForDate(otherCandidate, date);
      return otherApplication && otherApplication.status === 'SIGNED';
    });
    
    console.log(hasSignedApplicationForDate && (application.status == 'ACCEPTED' || application.status == 'PROPOSED'));
    
    // If there's a SIGNED application for this date and this candidate doesn't have SIGNED status,
    // return REJECTED
    if (hasSignedApplicationForDate && (application.status == 'ACCEPTED' || application.status == 'PROPOSED')) {
      application.status = 'REJECTED';
      return 'REJECTED';
    }
    
    return application.status;
  }

  getStatusTitle(candidate: Candidate, date: string): string {
    const application = this.getApplicationForDate(candidate, date);
    if (!application) return 'Pas de candidature';
    
    const statusMap: { [key: string]: string } = {
      'UNAVAILABLE': 'Non disponible',
      'NO_APPLICATION': 'Pas de candidature',
      'PROPOSED': 'Candidature proposée',
      'REJECTED': 'Candidature annulée car refusée ou déjà affectée',
      'CANCELLED': 'Candidature annulée',
      'ACCEPTED': 'Date acceptée non signée',
      'CONTRACT_PENDING_VALIDATION': 'Contrat en attente de validation',
      'SIGNED': 'Date affectée signée'
    };
    
    return statusMap[application.status] || application.status;
  }

  getStatusIcon(candidate: Candidate, date: string): string {
    const application = this.getApplicationForDate(candidate, date);
    if (!application) return '';
    return (application.status === 'REJECTED' || application.status === 'CANCELLED') ? '×' : '';
  }

  private getApplicationForDate(candidate: Candidate, date: string): CandidateApplication | null {
    return candidate.applications.find(app => 
      DateService.isDateInRange(date, app.fromDate, app.toDate)
    ) || null;
  }

  canAccept(candidate: Candidate): boolean {
    return this.mayRespond && candidate.applications.some(app => app.status === 'PROPOSED');
  }

  canReject(candidate: Candidate): boolean {
    return this.mayRespond && candidate.applications.some(app => app.status === 'PROPOSED');
  }

  canCancelAccepted(candidate: Candidate): boolean {
    return this.mayCancel && candidate.applications.some(app => app.status === 'ACCEPTED');
  }

  canCancelSigned(candidate: Candidate): boolean {
    return this.mayCancel && candidate.applications.some(app => app.status === 'SIGNED');
  }

  onAcceptCandidate(candidate: Candidate): void {
    this.acceptCandidate.emit(candidate);
  }

  onRejectCandidate(candidate: Candidate): void {
    this.rejectCandidate.emit(candidate);
  }

  onCancelAccepted(candidate: Candidate): void {
    this.cancelAccepted.emit(candidate);
  }

  onCancelSigned(candidate: Candidate): void {
    this.cancelSigned.emit(candidate);
  }

  openDetailCandidate = (candidate: any) => {
    console.log(candidate);
    
    this.dialog.open(DetailsComponent, {
      width: '666px',
      height: '100vh',
      position: { right: '0' },
      backdropClass: 'bg-transparent',
      data: {
        candidature: {candidatId: candidate.id, establishmentId: this.mission.establishmentId}
      }
    });
  };

  /**
   * Handle click on a cell
   * @param candidate The candidate
   * @param date The date
   */
  onCellClick(candidate: Candidate, date: string): void {
    const application = this.getApplicationForDate(candidate, date);
    if (!application) return;

    // Create a candidate with only the clicked application
    const candidateWithSingleApp: Candidate = {
      ...candidate,
      applications: [application]
    };

    switch (application.status) {
      case 'PROPOSED':
        if (!this.mayRespond) return;
        // Pour les cellules orange, émettre un événement pour ouvrir le modal avec options
        this.cellActionRequested.emit({
          candidate: candidateWithSingleApp,
          date: date,
          status: 'PROPOSED'
        });
        break;

      case 'CONTRACT_PENDING_VALIDATION':
        // Purple cell — open the edit/validate contract dialog
        this.editContractRequested.emit({
          candidate: candidateWithSingleApp,
          date,
          applicationId: application.id
        });
        break;

      case 'ACCEPTED':
        // Pour les cellules bleues
        if (!this.mayCancel) return;
        this.onCancelAccepted(candidateWithSingleApp);
        break;

      case 'SIGNED':
        // Pour les cellules vertes
        if (!this.mayCancel) return;
        this.onCancelSigned(candidateWithSingleApp);
        break;
    }
  }
  
  /**
   * Determine if contract button should be shown for a cell
   */
  shouldShowContractButton(candidate: Candidate, date: string): boolean {
    const application = this.getApplicationForDate(candidate, date);
    if (!application) return false;

    // Show button for ACCEPTED or SIGNED status
    return application.status === 'ACCEPTED' || application.status === 'SIGNED';
  }

  /**
   * Show the "Modifier et valider" button as long as at least one contract is still
   * pending validation. CONTRACT_PENDING_VALIDATION means neither is sent; ACCEPTED
   * means the mission contract has been sent but the disposition contract may still
   * be unsent (the dialog displays read-only state for contracts that are already sent).
   */
  shouldShowEditContractButton(candidate: Candidate, date: string): boolean {
    const application = this.getApplicationForDate(candidate, date);
    if (!application) return false;
    return application.status === 'CONTRACT_PENDING_VALIDATION' ||
           application.status === 'ACCEPTED' ||
           application.status === 'SIGNED'; 
  }

  onEditContract(candidate: Candidate, date: string, event?: MouseEvent): void {
    event?.stopPropagation();
    const application = this.getApplicationForDate(candidate, date);
    if (!application) return;
    this.editContractRequested.emit({
      candidate,
      date,
      applicationId: application.id
    });
  }

  /**
   * Show the contract preview using the getPreviewFragmentUrl API
   */
  showContract(candidate: Candidate, establishmentId: number, date: string, event?: MouseEvent): void {
    // Stop event propagation to prevent triggering the cell click
    event?.stopPropagation();
    
    if (!candidate || !candidate.id || !establishmentId) {
      console.error('Missing required parameters for contract preview');
      return;
    }
    
    // Get the application for the clicked date
    const application = this.getApplicationForDate(candidate, date);
    if (!application || !application.id) {
      console.error('Missing application id for contract preview');
      return;
    }
    
    // Show loader
    const button = event?.currentTarget as HTMLButtonElement;
    const originalContent = button.innerHTML;
    button.disabled = true;
    button.setAttribute("disabled", "true");
    button.innerHTML = '<p style="margin: 0 !important;">Chargement en cours...</p>';
    
    this.establishmentService.getPreviewFragmentUrl(
      candidate.id,
      establishmentId,
      application.id
    ).subscribe(
      (url) => {
        // Reset button
        button.disabled = false;
        button.innerHTML = originalContent;
        button.removeAttribute("disabled");

        // Redirect to the URL
        window.open(url, '_blank');
      },
      (error) => {
        // Reset button
        button.disabled = false;
        button.innerHTML = originalContent;
        button.removeAttribute("disabled");

        console.error('Error fetching contract preview URL:', error);
        alert('Impossible de récupérer le contrat. Veuillez réessayer plus tard.');
      }
    );
  }

  showContractDisposition(candidate: Candidate, establishmentId: number, date: string, event?: MouseEvent): void {
    event?.stopPropagation();
    
    if (!candidate || !candidate.id || !establishmentId) {
      console.error('Missing required parameters for contract disposition preview');
      return;
    }
    
    // Show loader
    const button = event?.currentTarget as HTMLButtonElement;
    const originalContent = button.innerHTML;
    button.disabled = true;
    button.setAttribute("disabled", "true");
    button.innerHTML = '<mat-icon class="contract-icon" style="animation: spin 2s linear infinite;">autorenew</mat-icon>';
    
    this.establishmentService.generateContractDisposition(
      candidate.id,
      this.mission.id,
      establishmentId
    ).subscribe({
      next: (data) => {
        button.disabled = false;
        button.innerHTML = originalContent;
        button.removeAttribute("disabled");
        
        if (data && data.contractBytes) {
          const byteCharacters = atob(data.contractBytes);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank', 'noopener, noreferrer');
        }
      },
      error: (err) => {
        button.disabled = false;
        button.innerHTML = originalContent;
        button.removeAttribute("disabled");
        console.error('Error fetching contract disposition:', err);
        alert('Impossible de récupérer le contrat. Veuillez réessayer plus tard.');
      }
    });
  }

  openMessageDialog(candidate: Candidate, event: MouseEvent) {        
    if (!candidate || !candidate.id) {
      console.error('Candidate data is missing', candidate);
      return;
    }

    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    const dialogWidth = 340;
    let left = rect.left + window.scrollX + rect.width / 2 - dialogWidth / 2;
    if (left < 8) left = 8;
    if (left + dialogWidth > window.innerWidth - 8) left = window.innerWidth - dialogWidth - 8;
    const top = rect.top + window.scrollY - 2;

    this.dialog.open(MessageDialogComponent, {
      width: dialogWidth + 'px',
      position: {
        top: `${rect.top}px`,
        left: `${left}px`
      },
      panelClass: ['chat-popover-panel', 'arrow-center'],
      data: {
        candidatId: candidate.id.toString(),
        candidatName: `${candidate.firstName} ${candidate.name}`,
        missionId: this.mission.id.toString(),
        missionTitle: this.mission.reference
      }
    });
  }
}